/**
 * =============================================================================
 * Main Application Component
 * =============================================================================
 *
 * Root component that sets up routing and renders the application layout.
 *
 * Routes:
 * - / : Dashboard (requires authentication)
 * - /institutions : Institutions management
 * - /devices : Devices management
 * - /hands : Hands management
 * - /local-users : Local users management (username/password auth)
 * - /service-accounts : Monitoring service accounts (system_admin only)
 * - /app-teams : App Teams management
 * - /app-teams/:id : App Team detail page
 * - /apps : Apps management (app developers and admins)
 * - /apps/new : Create new app wizard
 * - /apps/:id : App detail page
 * - /games : Legacy games management (kept for backward compatibility)
 *
 * Authentication:
 * All routes are protected by authentication.
 * Unauthenticated users see the login page.
 */

import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './auth/AuthProvider'

// Layout
import Layout from './components/Layout'
import LoadingScreen from './components/LoadingScreen'
import LoginPage from './pages/LoginPage'

// Pages
import Dashboard from './pages/Dashboard'
import InstitutionsPage from './pages/InstitutionsPage'
import DevicesPage from './pages/DevicesPage'
import HandsPage from './pages/HandsPage'
import LocalUsersPage from './pages/LocalUsersPage'
import InstitutionUsersPage from './pages/InstitutionUsersPage'
import AppTeamsPage from './pages/AppTeamsPage'
import AppTeamDetailPage from './pages/AppTeamDetailPage'
import AppsPage from './pages/AppsPage'
import AppForm from './pages/AppForm'
import AppDetailPage from './pages/AppDetailPage'
import DeviceTypesPage from './pages/DeviceTypesPage'
import RecordsPage from './pages/RecordsPage'
import ServiceAccountsPage from './pages/ServiceAccountsPage'

// =============================================================================
// Protected Route Component
// =============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: string[]
}

/**
 * Protected route wrapper.
 * Redirects to login if not authenticated.
 * Optionally checks for required roles.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles }) => {
  const { isAuthenticated, roles, initialized, logout, user } = useAuth()

  // Show loading while checking auth
  if (!initialized) {
    return <LoadingScreen />
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />
  }

  // Check role requirements
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => roles.includes(role))
    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="card text-center max-w-md">
            <h2 className="text-xl font-semibold text-red-600 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
            <div className="text-sm text-gray-500 mb-4">
              <p>User: {user?.email}</p>
              <p>Your roles: {roles.length > 0 ? roles.join(', ') : 'none'}</p>
              <p>Required: {requiredRoles.join(' or ')}</p>
            </div>
            <button
              onClick={logout}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}

// =============================================================================
// App Component
// =============================================================================

/**
 * Main application component.
 * Sets up routing and authentication checks.
 */
function App() {
  const { initialized } = useAuth()

  // Show loading screen while Keycloak initializes
  if (!initialized) {
    return <LoadingScreen />
  }

  return (
    <Routes>
      {/* Protected routes with layout */}
      {/*
        Keycloak Roles:
        - system_admin: Full platform access
        - rehapiano_admin: Platform administrator (devices, API keys, institutions)
        - institution_admin: Institution administrator (manages devices, hands)
        - app_admin: App administrator (manages apps, app teams, approvals)
        - app_dev: App developer (can develop and upload apps)
      */}
      <Route
        path="/"
        element={
          <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'institution_admin', 'app_admin', 'app_dev']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route index element={<Dashboard />} />

        {/* Institutions - system_admin and rehapiano_admin */}
        <Route
          path="institutions"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin']}>
              <InstitutionsPage />
            </ProtectedRoute>
          }
        />

        {/* Devices - system_admin, rehapiano_admin and institution_admin */}
        <Route
          path="devices"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'institution_admin']}>
              <DevicesPage />
            </ProtectedRoute>
          }
        />

        {/* Hands - system_admin, rehapiano_admin and institution_admin */}
        <Route
          path="hands"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'institution_admin']}>
              <HandsPage />
            </ProtectedRoute>
          }
        />

        {/* Local users - system_admin and rehapiano_admin */}
        <Route
          path="local-users"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin']}>
              <LocalUsersPage />
            </ProtectedRoute>
          }
        />

        {/* Service accounts (monitoring machine identities) - system_admin only */}
        <Route
          path="service-accounts"
          element={
            <ProtectedRoute requiredRoles={['system_admin']}>
              <ServiceAccountsPage />
            </ProtectedRoute>
          }
        />

        {/* Institution users (doctors / institution admins) */}
        <Route
          path="institution-users"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'institution_admin']}>
              <InstitutionUsersPage />
            </ProtectedRoute>
          }
        />

        {/* App Teams - all app-related roles */}
        <Route
          path="app-teams"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'app_admin', 'app_dev']}>
              <AppTeamsPage />
            </ProtectedRoute>
          }
        />

        {/* App Team Detail */}
        <Route
          path="app-teams/:id"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'app_admin', 'app_dev']}>
              <AppTeamDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Apps - app admins, developers, and system_admin */}
        <Route
          path="apps"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'app_admin', 'app_dev']}>
              <AppsPage />
            </ProtectedRoute>
          }
        />

        {/* Device Types - system_admin and app_admin */}
        <Route
          path="device-types"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'app_admin']}>
              <DeviceTypesPage />
            </ProtectedRoute>
          }
        />

        {/* App Wizard - developers and admins */}
        <Route
          path="apps/new"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'app_dev', 'app_admin']}>
              <AppForm />
            </ProtectedRoute>
          }
        />

        {/* App Detail */}
        <Route
          path="apps/:id"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'app_admin', 'app_dev']}>
              <AppDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Records Sync Health (aggregated, no patient data) - platform + institution admins */}
        <Route
          path="records"
          element={
            <ProtectedRoute requiredRoles={['system_admin', 'rehapiano_admin', 'institution_admin']}>
              <RecordsPage />
            </ProtectedRoute>
          }
        />

        {/* Legacy Games routes - redirect to /apps */}
        <Route path="games" element={<Navigate to="/apps" replace />} />
        <Route path="games/new" element={<Navigate to="/apps/new" replace />} />
        <Route path="games/:id" element={<Navigate to="/apps" replace />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
